import { RoomClass, RoomEnclosure, RoomType } from "building-types/rooms/roomTypesEnum";
import type { RoomAttributes } from "building-types/rooms/staticRoomAttributes";
import { BuiltinWindowId } from "building-types/windows/windowId";
import { roomAttributesNew } from "./roomAttributesTools";

export function defaultRoomAttributes(roomType: RoomType) {
  return defaultRoomCatalog[roomType] ?? roomAttributesNew({ roomType });
}

export const defaultRoomCatalog: { [type in RoomType]: RoomAttributes } = {
  MASTER_BED: roomAttributesNew({
    roomType: RoomType.MASTER_BED,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Owners",
    shortName: "Ownr Bd",
    windowType: BuiltinWindowId.T1,
    bedroomCount: 1,
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  MASTER_BATH: roomAttributesNew({
    roomType: RoomType.MASTER_BATH,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Owners bath",
    shortName: "Ownr Bth",
    windowType: BuiltinWindowId.A3050,
    bathroomCount: 1,
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  MASTER_CLOSET: roomAttributesNew({
    roomType: RoomType.MASTER_CLOSET,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Owners closet",
    shortName: "Ownr Cl",
    windowType: BuiltinWindowId.NO,
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  MASTER_HALL: roomAttributesNew({
    roomType: RoomType.MASTER_HALL,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Owners hall",
    shortName: "Ownr Hl",
    windowType: BuiltinWindowId.A3050,
    isCirculation: true,
    roomTagHiddenInPlan: true,
    roomSalesLabelHiddenInBrochure: true,
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  MASTER_PASS: roomAttributesNew({
    roomType: RoomType.MASTER_PASS,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Owners pass",
    shortName: "Pass",
    roomTagHiddenInPlan: true,
    roomSalesLabelHiddenInBrochure: true,
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  MASTER_SEAT: roomAttributesNew({
    roomType: RoomType.MASTER_SEAT,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Owners seating",
    shortName: "Seat",
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  MASTER_SUITE: roomAttributesNew({
    roomType: RoomType.MASTER_SUITE,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Owners suite",
    shortName: "Suite",
    hasBaseTrimMolding: true,
    hiddenInEditor: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  MASTER_VESTIBULE: roomAttributesNew({
    roomType: RoomType.MASTER_VESTIBULE,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Owners vestibule",
    shortName: "mVest",
    roomTagHiddenInPlan: true,
    roomSalesLabelHiddenInBrochure: true,
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  SHOWER: roomAttributesNew({
    roomType: RoomType.SHOWER,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Shower",
    shortName: "Shwr",
    windowType: BuiltinWindowId.NO,
    roomTagHiddenInPlan: true,
    roomSalesLabelHiddenInBrochure: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  WATER_CLOSET: roomAttributesNew({
    roomType: RoomType.WATER_CLOSET,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "WC",
    shortName: "WC",
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  BATH_HALL: roomAttributesNew({
    roomType: RoomType.BATH_HALL,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Bath hall",
    shortName: "bHall",
    openToAdjacent: true,
    roomTagHiddenInPlan: true,
    roomSalesLabelHiddenInBrochure: true,
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  MASTER_VANITY: roomAttributesNew({
    roomType: RoomType.MASTER_VANITY,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Owners vanity",
    shortName: "mBath",
    roomTagHiddenInPlan: true,
    roomSalesLabelHiddenInBrochure: true,
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  BEDROOM: roomAttributesNew({
    roomType: RoomType.BEDROOM,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Bedroom",
    shortName: "Bdrm",
    windowType: BuiltinWindowId.TW3050,
    bedroomCount: 1,
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  BED_PASS: roomAttributesNew({
    roomType: RoomType.BED_PASS,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Bedroom pass",
    shortName: "Pass",
    roomTagHiddenInPlan: true,
    roomSalesLabelHiddenInBrochure: true,
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  REACH_CLOSET: roomAttributesNew({
    roomType: RoomType.REACH_CLOSET,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Reach closet",
    shortName: "Cl",
    windowType: BuiltinWindowId.NO,
    roomTagHiddenInPlan: true,
    roomSalesLabelHiddenInBrochure: true,
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  BED_CLOSET: roomAttributesNew({
    roomType: RoomType.BED_CLOSET,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Closet",
    shortName: "Cl",
    windowType: BuiltinWindowId.NO,
    roomTagHiddenInPlan: true,
    roomSalesLabelHiddenInBrochure: true,
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  DINING: roomAttributesNew({
    roomType: RoomType.DINING,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Dining",
    shortName: "Dine",
    windowType: BuiltinWindowId.TW3050,
    openToAdjacent: true,
    hasBaseTrimMolding: true,
    lightIntensityOverride: 0.5,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  GUEST_SUITE: roomAttributesNew({
    roomType: RoomType.GUEST_SUITE,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Guest suite",
    shortName: "Guest",
    hasBaseTrimMolding: true,
    hiddenInEditor: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  WALK_IN_CLOSET: roomAttributesNew({
    roomType: RoomType.WALK_IN_CLOSET,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Walk-in closet",
    shortName: "Cl",
    windowType: BuiltinWindowId.NO,
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  POWDER: roomAttributesNew({
    roomType: RoomType.POWDER,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Powder",
    shortName: "Pwdr",
    windowType: BuiltinWindowId.A3050,
    bathroomCount: 0.5,
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  FORMAL_DINING: roomAttributesNew({
    roomType: RoomType.FORMAL_DINING,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Formal dining",
    shortName: "Dine",
    hasBaseTrimMolding: true,
    hiddenInEditor: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  FORMAL_LIVING: roomAttributesNew({
    roomType: RoomType.FORMAL_LIVING,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Living",
    shortName: "Live",
    hasBaseTrimMolding: true,
    hiddenInEditor: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  KITCHEN: roomAttributesNew({
    roomType: RoomType.KITCHEN,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Kitchen",
    shortName: "Kitch",
    windowType: BuiltinWindowId.NO,
    openToAdjacent: true,
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  KITCHEN_CABINETS: roomAttributesNew({
    roomType: RoomType.KITCHEN_CABINETS,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Kitchen",
    shortName: "Ktch",
    roomTagHiddenInPlan: true,
    roomSalesLabelHiddenInBrochure: true,
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  DINING_NOOK: roomAttributesNew({
    roomType: RoomType.DINING_NOOK,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Dining nook",
    shortName: "Dine Nk",
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  REFRIGERATOR: roomAttributesNew({
    roomType: RoomType.REFRIGERATOR,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Refrigerator",
    shortName: "Rfrg",
    roomTagHiddenInPlan: true,
    roomSalesLabelHiddenInBrochure: true,
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  KITCHEN_HALL: roomAttributesNew({
    roomType: RoomType.KITCHEN_HALL,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Kitchen hall",
    shortName: "Ktch Hl",
    isCirculation: true,
    roomTagHiddenInPlan: true,
    roomSalesLabelHiddenInBrochure: true,
    hasBaseTrimMolding: true,
    lightIntensityOverride: -1,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  PANTRY: roomAttributesNew({
    roomType: RoomType.PANTRY,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Pantry",
    shortName: "Pant",
    windowType: BuiltinWindowId.NO,
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  LIVING: roomAttributesNew({
    roomType: RoomType.LIVING,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Living room",
    shortName: "Living",
    openToAdjacent: true,
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  LIBRARY: roomAttributesNew({
    roomType: RoomType.LIBRARY,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Library",
    shortName: "Libr",
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  SCREEN: roomAttributesNew({
    roomType: RoomType.SCREEN,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Screen",
    shortName: "Scrn",
    roomTagHiddenInPlan: true,
    roomSalesLabelHiddenInBrochure: true,
    hasBaseTrimMolding: true,
    hiddenInEditor: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: false,
  }),
  LIVING_HALL: roomAttributesNew({
    roomType: RoomType.LIVING_HALL,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Living hall",
    shortName: "Scrn Hl",
    isCirculation: true,
    roomTagHiddenInPlan: true,
    roomSalesLabelHiddenInBrochure: true,
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  OFFICE: roomAttributesNew({
    roomType: RoomType.OFFICE,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Office",
    shortName: "Ofc",
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  BAY_WINDOW: roomAttributesNew({
    roomType: RoomType.BAY_WINDOW,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Bay window",
    shortName: "Bay",
    windowType: BuiltinWindowId.A3050,
    openToAdjacent: true,
    roomTagHiddenInPlan: true,
    roomSalesLabelHiddenInBrochure: true,
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  COAT_CLOSET: roomAttributesNew({
    roomType: RoomType.COAT_CLOSET,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Coat",
    shortName: "COAT",
    roomSalesLabelHiddenInBrochure: true,
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: false,
  }),
  BATHFULL: roomAttributesNew({
    roomType: RoomType.BATHFULL,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Bath",
    shortName: "Bath",
    windowType: BuiltinWindowId.A3050,
    bathroomCount: 1,
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  BATHFULL_1_DOOR: roomAttributesNew({
    roomType: RoomType.BATHFULL_1_DOOR,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Bath",
    shortName: "Bath ",
    windowType: BuiltinWindowId.A3050,
    singleDoor: true,
    hasBaseTrimMolding: true,
    hiddenInEditor: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  BATHHALF: roomAttributesNew({
    roomType: RoomType.BATHHALF,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Half bath",
    shortName: "Bath 0.5",
    bathroomCount: 0.5,
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  JJ_BATH: roomAttributesNew({
    roomType: RoomType.JJ_BATH,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Jack / Jill bath",
    shortName: "JJ Bth",
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  BED_BATH: roomAttributesNew({
    roomType: RoomType.BED_BATH,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Bath",
    shortName: "Bath",
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  BATHTUB: roomAttributesNew({
    roomType: RoomType.BATHTUB,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Bathtub",
    shortName: "Bath",
    roomTagHiddenInPlan: true,
    roomSalesLabelHiddenInBrochure: true,
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  ENTRY: roomAttributesNew({
    roomType: RoomType.ENTRY,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Entry",
    shortName: "Entry",
    openToAdjacent: true,
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  FOYER: roomAttributesNew({
    roomType: RoomType.FOYER,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Foyer",
    shortName: "Foyr",
    openToAdjacent: true,
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  ENTRY_BUFFER: roomAttributesNew({
    roomType: RoomType.ENTRY_BUFFER,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Entry buffer",
    shortName: "Bufr",
    roomTagHiddenInPlan: true,
    roomSalesLabelHiddenInBrochure: true,
    hasBaseTrimMolding: true,
    hiddenInEditor: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  OPEN_BELOW: roomAttributesNew({
    roomType: RoomType.OPEN_BELOW,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Open",
    shortName: "Open",
    windowType: BuiltinWindowId.TW3050,
    openToBelow: true,
    removeFloorStructure: true,
    openToAdjacent: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  GENERAL: roomAttributesNew({
    roomType: RoomType.GENERAL,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "General",
    shortName: "Gen",
    roomTagHiddenInPlan: true,
    roomSalesLabelHiddenInBrochure: true,
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  GALLERY: roomAttributesNew({
    roomType: RoomType.GALLERY,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Gallery",
    shortName: "Glry",
    windowType: BuiltinWindowId.TW3050,
    openToAdjacent: true,
    hasBaseTrimMolding: true,
    hiddenInEditor: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  GENERAL_OPEN: roomAttributesNew({
    roomType: RoomType.GENERAL_OPEN,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "General open",
    shortName: "Gen",
    windowType: BuiltinWindowId.A3050,
    openToAdjacent: true,
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: false,
  }),
  CLOSET: roomAttributesNew({
    roomType: RoomType.CLOSET,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Closet",
    shortName: "Cl",
    windowType: BuiltinWindowId.NO,
    roomTagHiddenInPlan: true,
    roomSalesLabelHiddenInBrochure: true,
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  SOLARIUM: roomAttributesNew({
    roomType: RoomType.SOLARIUM,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Solarium",
    shortName: "Solar",
    windowType: BuiltinWindowId.TW3050,
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  READING_NOOK: roomAttributesNew({
    roomType: RoomType.READING_NOOK,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Reading nook",
    shortName: "Nook",
    windowType: BuiltinWindowId.TW3050,
    openToAdjacent: true,
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  FLEX: roomAttributesNew({
    roomType: RoomType.FLEX,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Flex",
    shortName: "Flex",
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  CABINETS: roomAttributesNew({
    roomType: RoomType.CABINETS,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Cabinets",
    shortName: "Cab",
    roomTagHiddenInPlan: true,
    roomSalesLabelHiddenInBrochure: true,
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  HALL: roomAttributesNew({
    roomType: RoomType.HALL,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Hall",
    shortName: "Hl",
    windowType: BuiltinWindowId.A3050,
    openToAdjacent: true,
    isCirculation: true,
    roomTagHiddenInPlan: true,
    roomSalesLabelHiddenInBrochure: true,
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  HALL_NO_WINDOW: roomAttributesNew({
    roomType: RoomType.HALL_NO_WINDOW,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Hall no window",
    shortName: "Hl",
    windowType: BuiltinWindowId.NO,
    openToAdjacent: true,
    roomTagHiddenInPlan: true,
    roomSalesLabelHiddenInBrochure: true,
    hasBaseTrimMolding: true,
    hiddenInEditor: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  HALL_NO_ENTRY: roomAttributesNew({
    roomType: RoomType.HALL_NO_ENTRY,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Hall no entry",
    shortName: "Hl",
    windowType: BuiltinWindowId.NO,
    openToAdjacent: true,
    isCirculation: true,
    roomTagHiddenInPlan: true,
    roomSalesLabelHiddenInBrochure: true,
    hasBaseTrimMolding: true,
    hiddenInEditor: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  STAIRS: roomAttributesNew({
    roomType: RoomType.STAIRS,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Stairs",
    shortName: "Str",
    windowType: BuiltinWindowId.NO,
    openToBelow: true,
    removeFloorStructure: true,
    roomSalesLabelHiddenInBrochure: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  STAIRS_EDITOR: roomAttributesNew({
    roomType: RoomType.STAIRS_EDITOR,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Stairs",
    shortName: "Str",
    windowType: BuiltinWindowId.NO,
    openToBelow: true,
    removeFloorStructure: true,
    roomTagHiddenInPlan: true,
    roomSalesLabelHiddenInBrochure: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  CORNER_HALL: roomAttributesNew({
    roomType: RoomType.CORNER_HALL,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Corner hall",
    shortName: "Hl",
    openToAdjacent: true,
    roomTagHiddenInPlan: true,
    roomSalesLabelHiddenInBrochure: true,
    hasBaseTrimMolding: true,
    hiddenInEditor: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  HALL_CASED_OPENING: roomAttributesNew({
    roomType: RoomType.HALL_CASED_OPENING,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Hall cased",
    shortName: "Hl",
    roomTagHiddenInPlan: true,
    roomSalesLabelHiddenInBrochure: true,
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: false,
  }),
  GARAGE: roomAttributesNew({
    roomType: RoomType.GARAGE,
    roomClass: RoomClass.GARAGE,
    roomEnclosure: RoomEnclosure.UNCONDITIONED,
    name: "Garage",
    shortName: "Gar",
    windowType: BuiltinWindowId.NO,
    hasCurb: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: false,
  }),
  GARAGE_DETACH: roomAttributesNew({
    roomType: RoomType.GARAGE_DETACH,
    roomClass: RoomClass.GARAGE,
    roomEnclosure: RoomEnclosure.UNCONDITIONED,
    name: "Garage",
    shortName: "Gar",
    hasCurb: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: false,
  }),
  UTIL_HALL: roomAttributesNew({
    roomType: RoomType.UTIL_HALL,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Utility hall",
    shortName: "Utl Hl",
    windowType: BuiltinWindowId.A3050,
    isCirculation: true,
    roomTagHiddenInPlan: true,
    roomSalesLabelHiddenInBrochure: true,
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  MECH: roomAttributesNew({
    roomType: RoomType.MECH,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Mechanical",
    shortName: "Mech",
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  MUDROOM: roomAttributesNew({
    roomType: RoomType.MUDROOM,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Mudroom",
    shortName: "Mud",
    windowType: BuiltinWindowId.A3050,
    roomSalesLabelHiddenInBrochure: true,
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  DOG: roomAttributesNew({
    roomType: RoomType.DOG,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Dogroom",
    shortName: "Dogroom",
    roomSalesLabelHiddenInBrochure: true,
    hiddenInEditor: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  JUMP: roomAttributesNew({
    roomType: RoomType.JUMP,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Jump room",
    shortName: "Jump",
    roomTagHiddenInPlan: true,
    roomSalesLabelHiddenInBrochure: true,
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  LAUNDRY: roomAttributesNew({
    roomType: RoomType.LAUNDRY,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Laundry",
    shortName: "Laun",
    windowType: BuiltinWindowId.A3050,
    hasBaseTrimMolding: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  WORKSHOP: roomAttributesNew({
    roomType: RoomType.WORKSHOP,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Workshop",
    shortName: "Work",
    windowType: BuiltinWindowId.A3050,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  MUD_LAUNDRY: roomAttributesNew({
    roomType: RoomType.MUD_LAUNDRY,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Mud/laundry",
    shortName: "MudLndry",
    roomSalesLabelHiddenInBrochure: true,
    hasBaseTrimMolding: true,
    hiddenInEditor: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  FRONT_PORCH: roomAttributesNew({
    roomType: RoomType.FRONT_PORCH,
    roomClass: RoomClass.PORCH,
    roomEnclosure: RoomEnclosure.OPEN,
    name: "Porch",
    shortName: "Prch",
    exteriorCeilingMaterial: "porchCeiling0",
    windowType: BuiltinWindowId.NO,
  }),
  REAR_PORCH: roomAttributesNew({
    roomType: RoomType.REAR_PORCH,
    roomClass: RoomClass.PORCH,
    roomEnclosure: RoomEnclosure.OPEN,
    name: "Porch",
    shortName: "Prch",
    exteriorCeilingMaterial: "porchCeiling0",
    windowType: BuiltinWindowId.NO,
  }),
  PATIO: roomAttributesNew({
    roomType: RoomType.PATIO,
    roomClass: RoomClass.PATIO,
    roomEnclosure: RoomEnclosure.NONE,
    name: "Patio",
    shortName: "Pat",
    windowType: BuiltinWindowId.NO,
  }),
  WALK: roomAttributesNew({
    roomType: RoomType.WALK,
    roomClass: RoomClass.EXTERIOR,
    roomEnclosure: RoomEnclosure.NONE,
    name: "Walk",
    shortName: "Walk",
    roomTagHiddenInPlan: true,
    windowType: BuiltinWindowId.NO,
    roomSalesLabelHiddenInBrochure: true,
  }),
  BREEZEWAY: roomAttributesNew({
    roomType: RoomType.BREEZEWAY,
    roomClass: RoomClass.EXTERIOR,
    roomEnclosure: RoomEnclosure.OPEN,
    name: "Breezeway",
    shortName: "Brzwy",
    hasBaseTrimMolding: true,
  }),
  FIREPLACE: roomAttributesNew({
    roomType: RoomType.FIREPLACE,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Fireplace",
    shortName: "Fire",
    windowType: BuiltinWindowId.NO,
    openToAbove: true,
    roomTagHiddenInPlan: true,
    roomSalesLabelHiddenInBrochure: true,
    hasExteriorTrim: true,
  }),
  FREESTANDING_FIREPLACE: roomAttributesNew({
    roomType: RoomType.FREESTANDING_FIREPLACE,
    roomClass: RoomClass.EXTERIOR,
    roomEnclosure: RoomEnclosure.OPEN,
    name: "Fireplace",
    shortName: "Fire",
    openToAbove: true,
  }),
  PORTICO: roomAttributesNew({
    roomType: RoomType.PORTICO,
    roomClass: RoomClass.PORCH,
    roomEnclosure: RoomEnclosure.EXTERIOR,
    name: "Portico",
    shortName: "Prtc",
    openToBelow: true,
    removeFloorStructure: true,
    hasCurb: true,
    hasInteriorWallFacades: true,
    hasExteriorTrim: true,
  }),
  NONE: roomAttributesNew({
    roomType: RoomType.NONE,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "None",
    shortName: "NONE",
    windowType: BuiltinWindowId.NO,
    roomTagHiddenInPlan: true,
    roomSalesLabelHiddenInBrochure: true,
    hiddenInEditor: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: false,
  }),
  CHASE: roomAttributesNew({
    roomType: RoomType.CHASE,
    roomClass: RoomClass.HOUSE,
    roomEnclosure: RoomEnclosure.CONDITIONED,
    name: "Chase",
    shortName: "Chase",
    openToAbove: true,
    roomTagHiddenInPlan: true,
    roomSalesLabelHiddenInBrochure: true,
    hasExteriorTrim: true,
    hasCrownTrimMolding: true,
  }),
  LANDSCAPE: roomAttributesNew({
    roomType: RoomType.LANDSCAPE,
    roomClass: RoomClass.EXTERIOR,
    roomEnclosure: RoomEnclosure.NONE,
    name: "Landscape",
    openToAbove: true,
    openToBelow: true,
    removeFloorStructure: true,
    roomTagHiddenInPlan: true,
    roomSalesLabelHiddenInBrochure: true,
  }),
};
